import { BreakPoints, V, BP } from '@shared/helpers/breakpoints/BreakPoints';
import { getKeys } from '@shared/helpers/object.helpers';

/**
 * Project limit information for the pro plan.
 * @description The pro plan is priced based on the number of active projects.
 * An active project is a project that is not deleted, archived or over 1 year old.
 */
export const projectLimitInfo = [
  { activeProjects: '3', price: 340 },
  { activeProjects: '5', price: 570 },
  { activeProjects: '10', price: 990 },
  { activeProjects: '20', price: 1980 },
  { activeProjects: '40', price: 3360 },
  { activeProjects: '60', price: 5040 },
  { activeProjects: '100', price: 7640 },
] as const;

export type ActiveProjects =
  (typeof projectLimitInfo)[number]['activeProjects'];
export type ProPlanPrice = (typeof projectLimitInfo)[number]['price'];

type Plan = {
  plan: 'enterprise' | 'free';
  billedAnnually?: never;
  activeProjects?: never;
};

type ProPlan = {
  plan: 'pro';
  billedAnnually: boolean;
  activeProjects: ActiveProjects;
};

export type PlanData = Plan | ProPlan;

export type PaywallFeature =
  | 'create-project'
  | 'duplicate-project'
  | 'create-template-project'
  | 'archive-project'
  | 'unarchive-project'
  | 'export-project'
  | 'export-spreadsheet'
  | 'change-project-owner';

export const getFeatureArray = (
  featureObject: Record<PaywallFeature, boolean>,
): PaywallFeature[] =>
  getKeys(featureObject).filter((key) => featureObject[key]);

/** an array to keep track of which actions that increases project count - and therefore needs to check if max project count is passed or not */
export const projectIncreasingFeatures = getFeatureArray({
  'create-project': true,
  'duplicate-project': true,
  'create-template-project': false,
  'archive-project': false,
  'unarchive-project': true,
  'export-project': false,
  'export-spreadsheet': false,
  'change-project-owner': false,
});

export const proFeatures: readonly PaywallFeature[] = getFeatureArray({
  'create-project': true,
  'duplicate-project': true,
  'create-template-project': true,
  'archive-project': true,
  'unarchive-project': true,
  'export-project': true,
  'export-spreadsheet': true,
  'change-project-owner': true,
});

export const freePlanFeatures: readonly PaywallFeature[] = getFeatureArray({
  'create-project': true,
  'duplicate-project': false,
  'create-template-project': false,
  'archive-project': false,
  'unarchive-project': false,
  'export-project': false,
  'export-spreadsheet': false,
  'change-project-owner': false,
});

export const planTemplateThresholds: Record<PlanData['plan'], number> = {
  free: 0,
  pro: 1,
  enterprise: Infinity,
};

/**
 * Breakpoints for the paywall plan. A max project count of
 * - 1 means it's a free plan
 * - between 1 and 10 means it's a pro plan
 * - above 10 (or if there is no set project limit) means it's an enterprise plan
 */
export const paywallPlanBreakpoints = BreakPoints<PlanData['plan']>([
  V('free'),
  BP('1]'),
  V('pro'),
  BP('10]'),
  V('enterprise'),
]);
